import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Upload, 
  FileText, 
  Copy, 
  Check, 
  Loader2, 
  Sparkles,
  X,
  Settings2,
  LayoutGrid,
  Smartphone,
  Hash,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Image,
  ImagePlus,
  Download,
  Car,
  RefreshCw,
  Database,
  Palette,
  CheckCircle,
  Calendar,
  Settings,
  ExternalLink,
  Users,
  Building2,
  UserCog,
  Truck,
  ShoppingCart,
  ClipboardList,
  Bot,
  MessageCircle
} from "lucide-react";
import DatabaseTab from "./components/DatabaseTab";
import MassUploadTab from "./components/MassUploadTab";
import DatabaseRentaTab from "./components/DatabaseRentaTab";
import DesignRentaTab from "./components/DesignRentaTab";
import PublicationsTab from "./components/PublicationsTab";
import SettingsTab from "./components/SettingsTab";
import LeadsTab from "./components/LeadsTab";
import ChatbotMonitorTab from "./components/ChatbotMonitorTab";
import AppointmentsTab from "./components/AppointmentsTab";
import ClientsTab from "./components/ClientsTab";
import QuotationsTab from "./components/QuotationsTab";
import UsersTab from "./components/UsersTab";
import CompanyConfigTab from "./components/CompanyConfigTab";
import BusinessSelector from "./components/BusinessSelector";
import SuppliersTab from "./components/SuppliersTab";
import PurchaseOrdersTab from "./components/PurchaseOrdersTab";
import RentalConfigTab from "./components/RentalConfigTab";
import ComercializadoraTab from "./components/ComercializadoraTab";
import { useBusiness } from "./contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Business context
  const { currentBusiness, businessId, loading: businessLoading } = useBusiness();
  
  // Main navigation
  const [mainTab, setMainTab] = useState("design"); // design, database, publications, clients, quotations, users, settings, leads
  
  // Switch to appropriate tab when business changes
  useEffect(() => {
    if (businessId === "comercializadora") {
      setMainTab("comercializadora");
    } else if (mainTab === "comercializadora") {
      // Si estamos en comercializadora tab pero cambiamos a otro negocio, ir a design
      setMainTab("design");
    }
  }, [businessId]);
  
  // State for creating quotation from client
  const [quotationClient, setQuotationClient] = useState(null);
  
  // State
  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [customModel, setCustomModel] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [specifications, setSpecifications] = useState([]);
  
  // Vehicle image (automatically extracted from uploaded file)
  const [vehicleImage, setVehicleImage] = useState(null);
  // Imagen ORIGINAL extraída (para botón "restaurar la extraída")
  const [extractedVehicleImage, setExtractedVehicleImage] = useState(null);
  // Bandera: true cuando el usuario reemplazó manualmente la imagen
  const [isVehicleImageCustom, setIsVehicleImageCustom] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  // Índice actual durante generación secuencial inicial
  const [currentGeneratingIndex, setCurrentGeneratingIndex] = useState(-1);
  // Set de índices que están regenerando (permite múltiples simultáneos)
  const [regeneratingIndices, setRegeneratingIndices] = useState(new Set());
  
  // Ficha técnica URL (para descargar)
  const [specSheetUrl, setSpecSheetUrl] = useState(null);
  
  // Precio de compra (interno)
  const [purchasePrice, setPurchasePrice] = useState("");
  
  // Generated content
  const [carousel, setCarousel] = useState([]);
  const [promptStories, setPromptStories] = useState("");
  const [copyFeed, setCopyFeed] = useState("");
  const [copyStories, setCopyStories] = useState("");
  const [hashtagsFeed, setHashtagsFeed] = useState([]);
  const [hashtagsStories, setHashtagsStories] = useState([]);
  
  // UI state
  const [copiedItem, setCopiedItem] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState("feed");
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    
    // VALIDAR PRIMERO que el negocio esté seleccionado
    if (!businessId) {
      toast.error("Error: No se ha seleccionado un negocio. Por favor selecciona un negocio primero.");
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Tipo de archivo no soportado. Usa PDF, PNG, JPG o WEBP.");
      return;
    }

    console.log("=== FILE UPLOAD STARTED ===");
    console.log("Business ID at upload time:", businessId);
    console.log("Business Name:", currentBusiness?.name);

    setFile(selectedFile);
    setIsUploading(true);
    resetContent();

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFileInfo(response.data);
      toast.success("Archivo subido correctamente");
      
      // Pasar el businessId explícitamente para evitar problemas de closure
      await extractSpecs(response.data, businessId);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Error al subir el archivo");
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const resetContent = () => {
    setExtractedData(null);
    setCarousel([]);
    setPromptStories("");
    setCopyFeed("");
    setCopyStories("");
    setHashtagsFeed([]);
    setHashtagsStories([]);
    setSpecifications([]);
    setSelectedVersion("");
    setCustomModel("");
    setActiveCarouselIndex(0);
    setVehicleImage(null);
    setExtractedVehicleImage(null);
    setIsVehicleImageCustom(false);
    setGeneratedImages([]);
    setCurrentGeneratingIndex(-1);
    setRegeneratingIndices(new Set());
  };

  // Pool de 20 estilos para imágenes web (sin texto) - VARIEDAD MÁXIMA
  // POOL AUTOS
  const WEB_IMAGE_STYLES = [
    // Estudios y Showrooms
    { name: "Showroom Elegante", style: "luxury car showroom with clean white background, professional studio lighting, high-end dealership atmosphere, reflective floor" },
    { name: "Estudio Negro", style: "dramatic black studio background with spotlight lighting, elegant shadows, premium automotive photography, sleek presentation" },
    { name: "Showroom Moderno", style: "contemporary minimalist showroom with concrete walls, industrial chic design, soft natural lighting from large windows" },
    
    // Carreteras y Paisajes
    { name: "Carretera Montaña", style: "winding mountain road with dramatic peaks in background, golden hour sunlight, epic landscape, adventure feeling" },
    { name: "Autopista Atardecer", style: "modern highway at sunset with orange and purple sky, motion blur background, dynamic speed feeling" },
    { name: "Camino Costero", style: "scenic coastal road with ocean view, cliff side drive, blue water and sky, Mediterranean vibes" },
    { name: "Ruta Desierto", style: "desert highway with red rock formations, dramatic southwestern landscape, clear blue sky, vast open space" },
    
    // Urbano y Ciudad
    { name: "Ciudad Moderna", style: "sleek modern cityscape with glass skyscrapers, contemporary architecture, clean urban environment, business district" },
    { name: "Downtown Nocturno", style: "vibrant downtown at night with city lights, neon signs reflecting, wet streets after rain, cinematic mood" },
    { name: "Plaza Europea", style: "elegant European city square with historic architecture, cobblestone streets, sophisticated urban setting" },
    { name: "Distrito Financiero", style: "prestigious financial district with modern towers, professional corporate environment, sunrise lighting" },
    
    // Naturaleza
    { name: "Bosque Verde", style: "lush green forest road with tall trees forming natural tunnel, dappled sunlight, peaceful nature setting" },
    { name: "Lago Sereno", style: "calm lake shore with mountain reflections, pristine nature, early morning mist, tranquil atmosphere" },
    { name: "Pradera Dorada", style: "golden wheat field at sunset, rolling hills, warm country landscape, pastoral beauty" },
    { name: "Montaña Nevada", style: "majestic snow-capped mountains in background, alpine setting, crisp winter air, pristine white snow" },
    
    // Nocturno y Dramático
    { name: "Noche Urbana", style: "dramatic night scene with city lights bokeh, wet reflective streets, cinematic lighting, luxury night atmosphere" },
    { name: "Garage Premium", style: "upscale private garage with mood lighting, concrete and steel design, exclusive collector atmosphere" },
    { name: "Túnel Iluminado", style: "modern illuminated tunnel with dramatic light trails, futuristic atmosphere, speed and technology feeling" },
    
    // Especiales
    { name: "Helipuerto", style: "rooftop helipad with city skyline panorama, executive luxury setting, penthouse level, exclusive atmosphere" },
    { name: "Marina Lujosa", style: "luxury marina with yachts in background, waterfront setting, sophisticated coastal lifestyle, blue water" }
  ];

  // POOL MAQUINARIA — contextos industriales 100% creíbles para equipo pesado.
  // Ningún contexto urbano/ocio — solo escenarios reales de obra/industria.
  const MAQUINARIA_IMAGE_STYLES = [
    // Construcción y obra
    { name: "Cantera de Roca", style: "open-pit rock quarry with towering stone walls, dust in the air, dramatic daylight, rugged industrial landscape" },
    { name: "Mina a Cielo Abierto", style: "massive open-pit mine with terraced earth, heavy machinery activity nearby, dramatic overcast sky, industrial scale" },
    { name: "Construcción Urbana", style: "active urban construction site with steel frames of buildings under construction, cranes in background, morning light" },
    { name: "Pavimentación Carretera", style: "highway paving site with fresh asphalt, road work zone, orange traffic cones, sunset lighting" },
    { name: "Almacén Logístico", style: "large industrial logistics yard with stacked containers and pallets, warehouse buildings, overcast diffuse light" },
    { name: "Aeropuerto en Obra", style: "airport runway construction or expansion, wide open paved areas, airfield lights, dramatic horizon sky" },
    { name: "Refinería Industrial", style: "industrial oil refinery complex with pipes and stacks in background, industrial haze, golden hour light" },
    { name: "Demolición", style: "building demolition site with rubble and concrete debris, broken walls, dust clouds, dramatic contrasty lighting" },
    { name: "Movimiento de Tierras", style: "massive earthworks site with piles of soil and aggregates, freshly graded terrain, dramatic golden hour sky" },
    { name: "Muelle Portuario", style: "active sea port with cargo cranes and shipping containers, dock surface, ocean and industrial ships in background" },
    { name: "Desmonte Forestal", style: "forestry clearing site with felled logs and stripped land, forest edge in background, morning mist and sunlight" },
    { name: "Túnel Subterráneo", style: "underground tunnel construction with exposed rock walls, industrial work lights, dust particles, dramatic atmosphere" },
    { name: "Agroindustria", style: "large-scale farm or agribusiness field with agricultural infrastructure, cultivated fields, clear sky, rural industrial scale" },
    { name: "Túnel Ferroviario", style: "railway tunnel under construction with exposed concrete lining, track bed, work lights, industrial engineering scale" },
    { name: "Puente en Construcción", style: "bridge construction site with steel girders and concrete pillars, river or valley below, dramatic engineering perspective" },
    { name: "Excavación Cimentación", style: "deep foundation excavation pit with exposed rebar and concrete walls, heavy earth-moving equipment nearby, industrial construction site" },
    { name: "Canal Hidráulico", style: "hydraulic canal or drainage construction, concrete channel with water works, industrial infrastructure scale" },
    { name: "Zona de Acopio", style: "aggregate stockpile yard with huge piles of gravel and sand, industrial storage site, clear blue sky with dramatic clouds" },
    { name: "Planta de Concreto", style: "concrete batching plant with silos and mixing tower, industrial yard with trucks, overcast dramatic sky" },
    { name: "Amanecer en Obra", style: "construction job site at sunrise with long shadows, dramatic warm light on equipment and earth, epic industrial atmosphere" }
  ];

  // Seleccionar 3 estilos aleatorios diferentes según el negocio
  const getRandomWebStyles = () => {
    const pool = (currentBusiness?.id === "maquinaria") ? MAQUINARIA_IMAGE_STYLES : WEB_IMAGE_STYLES;
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  };

  // Generate images with Hailuo (using automatically extracted image)
  const generateImages = async () => {
    if (!vehicleImage || carousel.length === 0) {
      toast.error("Se necesita la imagen del vehiculo y los prompts generados");
      return;
    }
    
    setIsGeneratingImages(true);
    setGeneratedImages([]);
    setCurrentGeneratingIndex(0);
    
    try {
      // Seleccionar 3 estilos aleatorios para las imágenes web
      const selectedWebStyles = getRandomWebStyles();
      const isMaquinaria = currentBusiness?.id === "maquinaria";
      const brandUp = (extractedData?.brand || "").toUpperCase();
      const modelUp = (customModel || "").toUpperCase();
      const subjectName = isMaquinaria
        ? `${modelUp} ${brandUp}`.trim()
        : `${brandUp} ${modelUp}`.trim();
      const photographyStyle = isMaquinaria
        ? "Professional heavy machinery / industrial equipment photography"
        : "Professional automotive photography";
      const noTextClean = isMaquinaria
        ? "Clean commercial industrial photography, NO TEXT, NO OVERLAYS, NO WATERMARKS, NO GRAPHICS. Just the machinery in high quality, photorealistic, 8K resolution, professional equipment advertisement."
        : "Clean commercial photography, NO TEXT, NO OVERLAYS, NO WATERMARKS, NO GRAPHICS. Just the vehicle in high quality, photorealistic, 8K resolution, professional car advertisement.";

      // Pool de escenarios variados (mismos que usa el Trabajador Virtual en el backend)
      // para que las imágenes web NO salgan todas en el mismo entorno.
      const AUTOMOTRIZ_SCENES = [
        "coastal cliffside road at golden hour with ocean below",
        "modern city skyline at sunset with skyscrapers in background",
        "mountain switchback road with pine forest and dramatic sky",
        "desert highway with rock formations and warm sunset light",
        "urban rooftop parking with city lights and neon glow at dusk",
        "empty airport tarmac at sunrise with warm golden light",
        "modern architectural plaza with concrete and glass buildings",
        "countryside vineyard road with rolling hills and evening light",
        "abandoned industrial complex with rusted structures and dramatic clouds",
        "high-altitude alpine road with snowy peaks in background",
        "downtown financial district street at blue hour with wet pavement reflections",
        "seaside boardwalk at golden hour with palm trees",
        "canyon road with red rock formations and dramatic shadows",
        "modern minimalist showroom with polished concrete floor and moody lighting",
        "urban tunnel exit at sunset with dramatic light streaks",
      ];
      const MAQUINARIA_SCENES = [
        "open-pit rock quarry with stone walls and dust in the air",
        "active urban construction site with steel building frames and cranes",
        "highway paving site with fresh asphalt and orange traffic cones",
        "industrial logistics yard with stacked containers and warehouses",
        "airport runway under construction with airfield lights",
        "demolition site with rubble, broken walls and dust clouds",
        "massive earthworks site with piles of soil and graded terrain",
        "active sea port with cargo cranes and shipping containers",
        "bridge construction site with steel girders and concrete pillars",
        "deep foundation excavation pit with rebar and concrete walls",
        "concrete batching plant with silos and mixing tower",
        "construction job site at sunrise with long warm shadows",
      ];
      const SCENE_POOL = isMaquinaria ? MAQUINARIA_SCENES : AUTOMOTRIZ_SCENES;
      // Baraja el pool y toma escenas únicas por imagen web
      const shuffled = [...SCENE_POOL].sort(() => Math.random() - 0.5);

      // Crear prompts para imágenes web LIMPIAS (sin texto, sin overlays)
      // Cada estilo web recibe UN ESCENARIO ALEATORIO DIFERENTE — antes usaban `style.style`
      // fijo por lo que todas salían en el mismo entorno. Ahora igualamos al Trabajador Virtual.
      const webImagePrompts = selectedWebStyles.map((style, idx) => {
        const scene = shuffled[idx % shuffled.length];
        return {
          name: style.name,
          prompt: `${photographyStyle} of a ${subjectName} ${selectedVersion}.
Setting: ${scene}. Framing per style: ${style.style}.
Style: ${noTextClean}`,
          ratio: "16:9",
          type: "web"
        };
      });
      
      // Prepare prompts and ratios (4 carousel + 1 story + 3 web)
      const allImages = [
        ...carousel.map(c => ({ ...c, type: "carousel" })),
        { name: "Stories", prompt: promptStories, ratio: "9:16", type: "stories" },
        ...webImagePrompts
      ];
      
      const totalImages = allImages.length;
      toast.info(`Iniciando generación de ${totalImages} imágenes (5 redes + 3 web)...`);
      
      // Generate images one by one to show progress
      const results = [];
      for (let i = 0; i < allImages.length; i++) {
        const img = allImages[i];
        setCurrentGeneratingIndex(i);
        toast.info(`Generando ${img.name}... (${i + 1}/${totalImages})`);
        
        try {
          const response = await axios.post(`${API}/generate-image`, {
            prompt: img.prompt,
            aspect_ratio: img.ratio || "4:5",
            vehicle_image_base64: vehicleImage
          });
          
          results.push({
            index: i,
            name: img.name,
            ratio: img.ratio || "4:5",
            type: img.type,
            result: response.data
          });
          
          // Update state progressively
          setGeneratedImages([...results]);
          
        } catch (err) {
          results.push({
            index: i,
            name: img.name,
            ratio: img.ratio || "4:5",
            type: img.type,
            result: { success: false, error: err.message }
          });
          setGeneratedImages([...results]);
        }
        
        // Delay between requests
        if (i < allImages.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
      
      setCurrentGeneratingIndex(-1);
      toast.success("Generación de imágenes completada!");
      
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error("Error al generar imagenes: " + error.message);
    } finally {
      setIsGeneratingImages(false);
      setCurrentGeneratingIndex(-1);
    }
  };

  // Regenerate a single image - PERMITE MÚLTIPLES SIMULTÁNEAS
  const regenerateImage = async (index) => {
    if (!vehicleImage) {
      toast.error("Falta la imagen del vehículo");
      return;
    }
    
    // Verificar si ya está regenerando esta imagen específica
    if (regeneratingIndices.has(index)) {
      toast.info("Esta imagen ya se está regenerando...");
      return;
    }
    
    // Obtener datos de la imagen actual
    const currentImage = generatedImages[index];
    if (!currentImage) {
      toast.error("No se encontró la imagen a regenerar");
      return;
    }
    
    // Para imágenes web, generar un nuevo estilo aleatorio
    let prompt = currentImage.prompt;
    let newName = currentImage.name;
    
    if (currentImage.type === "web") {
      // Seleccionar un estilo aleatorio diferente del POOL correcto según el negocio
      const isMaquinaria = currentBusiness?.id === "maquinaria";
      const pool = isMaquinaria ? MAQUINARIA_IMAGE_STYLES : WEB_IMAGE_STYLES;
      const randomStyle = pool[Math.floor(Math.random() * pool.length)];
      newName = randomStyle.name;
      const brandUp = (extractedData?.brand || "").toUpperCase();
      const modelUp = (customModel || "").toUpperCase();
      const subjectName = isMaquinaria
        ? `${modelUp} ${brandUp}`.trim()
        : `${brandUp} ${modelUp}`.trim();
      const photographyStyle = isMaquinaria
        ? "Professional heavy machinery / industrial equipment photography"
        : "Professional automotive photography";
      const noTextClean = isMaquinaria
        ? "Clean commercial industrial photography, NO TEXT, NO OVERLAYS, NO WATERMARKS, NO GRAPHICS. Just the machinery in high quality, photorealistic, 8K resolution, professional equipment advertisement."
        : "Clean commercial photography, NO TEXT, NO OVERLAYS, NO WATERMARKS, NO GRAPHICS. Just the vehicle in high quality, photorealistic, 8K resolution, professional car advertisement.";
      prompt = `${photographyStyle} of a ${subjectName} ${selectedVersion}. 
Setting: ${randomStyle.style}. 
Style: ${noTextClean}`;
    } else if (!prompt) {
      // Para imágenes de redes sociales, reconstruir el prompt
      if (index < carousel.length) {
        prompt = carousel[index].prompt;
        newName = carousel[index].name;
      } else if (index === carousel.length) {
        prompt = promptStories;
        newName = "Stories";
      }
    }
    
    toast.info(`Regenerando ${newName}...`);
    
    // Agregar este índice al set de regenerando
    setRegeneratingIndices(prev => new Set([...prev, index]));
    
    try {
      const response = await axios.post(`${API}/generate-image`, {
        prompt: prompt,
        aspect_ratio: currentImage.ratio || "4:5",
        vehicle_image_base64: vehicleImage
      });
      
      // Update only that image in the results
      setGeneratedImages(prev => {
        const newImages = [...prev];
        newImages[index] = {
          index: index,
          name: newName,
          ratio: currentImage.ratio,
          type: currentImage.type,
          prompt: prompt,
          result: response.data
        };
        return newImages;
      });
      
      if (response.data.success) {
        toast.success(`${newName} regenerada exitosamente!`);
      } else {
        toast.error(`Error al regenerar ${newName}`);
      }
    } catch (err) {
      toast.error("Error: " + err.message);
    } finally {
      // Remover este índice del set
      setRegeneratingIndices(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  // Extract specifications
  const extractSpecs = async (uploadedFileInfo, explicitBusinessId) => {
    // Usar el businessId pasado explícitamente para evitar problemas de closure
    const currentBusinessId = explicitBusinessId || businessId;
    if (!currentBusinessId) {
      toast.error("Error: No se ha seleccionado un negocio. Por favor selecciona un negocio primero.");
      return;
    }
    
    console.log("=== EXTRACTING SPECS ===");
    console.log("Current Business ID:", currentBusinessId);
    console.log("Business Name:", currentBusiness?.name);
    
    setIsExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file_id', uploadedFileInfo.file_id);
      formData.append('filename', uploadedFileInfo.filename);

      const response = await axios.post(`${API}/extract-specs`, formData);

      if (response.data.success) {
        const data = response.data.data;
        setExtractedData(data);
        
        // Guardar URL de la ficha técnica
        if (response.data.spec_sheet_url) {
          setSpecSheetUrl(response.data.spec_sheet_url);
        }
        
        // Save extracted vehicle image
        if (response.data.vehicle_image) {
          setVehicleImage(response.data.vehicle_image);
          setExtractedVehicleImage(response.data.vehicle_image);
          setIsVehicleImageCustom(false);
          toast.success("Imagen del vehiculo extraida automaticamente");
        }
        
        // Manejar nuevo formato con versiones separadas
        if (data.versions) {
          const versionKeys = Object.keys(data.versions);
          if (versionKeys.length > 0) {
            const firstVersionKey = versionKeys[0];
            const firstVersion = data.versions[firstVersionKey];
            setSelectedVersion(String(firstVersion.name || firstVersionKey).toUpperCase());
            setSpecifications(firstVersion.specifications || []);
            
            // NO guardamos los vehículos aquí
            // Se guardarán SOLO cuando el usuario confirme el contenido
            console.log("=== EXTRACTED SPECS ===");
            console.log("Business ID:", currentBusinessId);
            console.log("Brand:", data.brand);
            console.log("Model:", data.model_name);
            console.log("Versions found:", versionKeys.length);
          }
        } else if (data.specifications) {
          // Formato antiguo de respaldo
          setSpecifications(data.specifications || []);
          if (data.available_versions?.length > 0) {
            setSelectedVersion(String(data.available_versions[0]).toUpperCase());
          }
        }
        
        if (data.model_name) {
          setCustomModel(String(data.model_name).toUpperCase());
        }
        toast.success("Especificaciones extraidas correctamente");
      } else {
        toast.error("Error al extraer especificaciones");
        // Still try to use extracted image even if specs failed
        if (response.data.vehicle_image) {
          setVehicleImage(response.data.vehicle_image);
          setExtractedVehicleImage(response.data.vehicle_image);
          setIsVehicleImageCustom(false);
        }
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error("Error al procesar el archivo");
    } finally {
      setIsExtracting(false);
    }
  };

  // Generate all content
  const generateContent = async () => {
    // VERIFICAR que el businessId esté correctamente establecido
    const currentBusinessId = currentBusiness?.id;
    if (!currentBusinessId) {
      toast.error("Error: No se ha seleccionado un negocio");
      return;
    }
    
    if (!customModel || !price || specifications.length === 0) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    console.log("=== GENERATING CONTENT ===");
    console.log("Business ID:", currentBusinessId);
    
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-prompt`, {
        model_name: customModel,
        version: selectedVersion || extractedData?.year || "",
        year: extractedData?.year || "",
        price: price,
        specifications: specifications.slice(0, 9),
        business_id: currentBusinessId, // USA EL VALOR VERIFICADO
        brand: extractedData?.brand || ""
      });

      if (response.data.success) {
        // Incluir la imagen Web al principio si existe.
        // Importante: forzar ratio 16:9 horizontal para que coincida con las
        // 3 imágenes web del pool de estilos. Antes salía en 4:5 (default
        // del carrusel) y se veía desproporcionada en el catálogo.
        const allImages = [];
        if (response.data.web_catalog) {
          allImages.push({
            ...response.data.web_catalog,
            ratio: "16:9",
            type: "web",
          });
        }
        allImages.push(...(response.data.carousel || []));
        
        setCarousel(allImages);
        setPromptStories(response.data.prompt_stories);
        setCopyFeed(response.data.copy_feed);
        setCopyStories(response.data.copy_stories);
        setHashtagsFeed(response.data.hashtags_feed || []);
        setHashtagsStories(response.data.hashtags_stories || []);
        setActiveCarouselIndex(0);
        toast.success(`Contenido generado para ${currentBusiness?.name || currentBusinessId}`);
        
        // Actualizar precio del vehículo en la base de datos (solo para este negocio)
        try {
          const vehiclesRes = await axios.get(`${API}/vehicles?business_id=${currentBusinessId}`);
          if (vehiclesRes.data.success) {
            const vehicle = vehiclesRes.data.vehicles.find(v => 
              v.model_name === customModel && v.version === selectedVersion
            );
            if (vehicle) {
              await axios.put(`${API}/vehicles/${vehicle.id}`, { price: price });
              console.log(`Precio actualizado para vehículo ${vehicle.id}: ${price}`);
            } else {
              console.log(`Vehículo no encontrado: ${customModel} - ${selectedVersion}`);
            }
          }
        } catch (priceErr) {
          console.error("Error actualizando precio:", priceErr);
        }
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error("Error al generar el contenido");
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, itemKey) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemKey);
    setTimeout(() => setCopiedItem(""), 2000);
    toast.success("Copiado al portapapeles");
  };

  // Copy hashtags
  const copyHashtags = (hashtags, itemKey) => {
    const text = hashtags.join(" ");
    copyToClipboard(text, itemKey);
  };

  // Confirm content and save to publications
  const [confirmProgress, setConfirmProgress] = useState(null); // null | {step, total, message, status: 'running'|'success'|'error'}

  const confirmContent = async () => {
    // VERIFICAR que el businessId esté correctamente establecido
    const currentBusinessId = currentBusiness?.id;
    if (!currentBusinessId) {
      toast.error("Error: No se ha seleccionado un negocio");
      return;
    }
    
    if (generatedImages.length === 0 || !copyFeed) {
      toast.error("Genera las imagenes primero antes de confirmar");
      return;
    }
    
    if (!price) {
      toast.error("Ingresa el precio antes de confirmar");
      return;
    }

    console.log("=== CONFIRMING PUBLICATION ===");
    console.log("Business ID:", currentBusinessId);

    setConfirmProgress({ step: 1, total: 3, message: "Preparando imágenes...", status: 'running' });

    try {
      // Mapear las imágenes generadas con sus tipos correctos
      const images = generatedImages.map((img) => {
        // Usar el tipo que ya viene en el objeto (carousel, stories, web)
        const type = img.type || "carousel";
        
        return {
          name: img.name,
          url: img.result?.data?.url || "",
          type: type
        };
      });
      
      console.log("Images to save:", images.map(i => ({ name: i.name, type: i.type })));
      
      // Separar imágenes por tipo
      const webImages = images.filter(img => img.type === "web");
      const carouselImages = images.filter(img => img.type === "carousel");
      const storiesImages = images.filter(img => img.type === "stories");
      
      // La primera imagen web será la principal
      const webImageUrl = webImages.length > 0 ? webImages[0].url : "";

      // PASO 1: Guardar el vehículo en la base de datos
      const vehicleData = {
        business_id: currentBusinessId,
        brand: extractedData?.brand || "",
        model_name: customModel,
        version: selectedVersion,
        year: extractedData?.year || "",
        price: price,
        purchase_price: purchasePrice,  // Precio de compra (interno)
        category: category,  // Categoría manual (si vacía, backend auto-detecta)
        spec_sheet_url: specSheetUrl || "",  // URL de la ficha técnica
        specifications: specifications,
        vehicle_image: vehicleImage || "",
        web_image: webImageUrl,  // Primera imagen web como principal
        web_gallery_images: webImages,  // Las 3 imágenes web limpias
        gallery_images: carouselImages  // Imágenes de redes sociales
      };
      
      console.log("Saving vehicle:", vehicleData.model_name, vehicleData.version);
      setConfirmProgress({ step: 2, total: 3, message: "Guardando vehículo en la base de datos...", status: 'running' });
      
      const vehicleResponse = await axios.post(`${API}/vehicles/create-single`, vehicleData);
      const vehicleId = vehicleResponse.data?.vehicle?.id || "";
      
      console.log("Vehicle saved with ID:", vehicleId);

      // PASO 2: Guardar la publicación
      const publicationData = {
        business_id: currentBusinessId,
        vehicle_id: vehicleId,
        vehicle_name: `${customModel} ${selectedVersion}`,
        images: images,
        copy_feed: copyFeed,
        copy_stories: copyStories,
        hashtags_feed: hashtagsFeed,
        hashtags_stories: hashtagsStories
      };

      console.log("Publication data:", publicationData);
      setConfirmProgress({ step: 3, total: 3, message: "Guardando publicación...", status: 'running' });

      const response = await axios.post(`${API}/publications/confirm`, publicationData);
      
      if (response.data.success) {
        setConfirmProgress({
          step: 3,
          total: 3,
          message: `¡Listo! Vehículo y publicación guardados en ${currentBusiness?.name || currentBusinessId}.`,
          status: 'success',
        });
        toast.success(`Vehículo y publicación guardados en ${currentBusiness?.name || currentBusinessId}`);
      }
    } catch (error) {
      console.error('Error confirming content:', error);
      const errMsg = error.response?.data?.detail || error.message;
      setConfirmProgress({
        step: 0,
        total: 3,
        message: `Error al confirmar contenido: ${errMsg}`,
        status: 'error',
      });
      toast.error("Error al confirmar contenido: " + errMsg);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  // Handle version change - cargar specs de la versión seleccionada
  const handleVersionChange = (versionName) => {
    setSelectedVersion(String(versionName).toUpperCase());
    
    // Buscar las especificaciones de esta versión
    if (extractedData?.versions) {
      const versionEntry = Object.entries(extractedData.versions).find(
        ([key, val]) => val.name === versionName || key === versionName
      );
      if (versionEntry) {
        setSpecifications(versionEntry[1].specifications || []);
        toast.success(`Especificaciones de ${versionName} cargadas`);
      }
    }
  };

  // Get available versions from extracted data
  const getAvailableVersions = () => {
    if (extractedData?.versions) {
      return Object.entries(extractedData.versions).map(([key, val]) => val.name || key);
    }
    if (extractedData?.available_versions) {
      return extractedData.available_versions;
    }
    return [];
  };

  // Specification handlers
  const updateSpec = (index, value) => {
    const newSpecs = [...specifications];
    newSpecs[index] = value;
    setSpecifications(newSpecs);
  };

  const removeSpec = (index) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const addSpec = () => {
    if (specifications.length < 9) {
      setSpecifications([...specifications, ""]);
    }
  };

  const clearAll = () => {
    setFile(null);
    setFileInfo(null);
    setPrice("");
    setPurchasePrice("");
    setCategory("");
    setSpecSheetUrl(null);
    resetContent();
  };

  // Carousel navigation
  const nextCarousel = () => {
    setActiveCarouselIndex((prev) => (prev + 1) % carousel.length);
  };

  const prevCarousel = () => {
    setActiveCarouselIndex((prev) => (prev - 1 + carousel.length) % carousel.length);
  };

  const hasContent = carousel.length > 0 || promptStories;
  const currentCopy = activeTab === "feed" ? copyFeed : copyStories;
  const currentHashtags = activeTab === "feed" ? hashtagsFeed : hashtagsStories;

  return (
    <div className="min-h-screen bg-[#0B1623]">
      <Toaster 
        theme="dark" 
        position="top-right"
        toastOptions={{
          style: {
            background: '#112233',
            border: '1px solid #1E293B',
            color: '#FFFFFF'
          }
        }}
      />
      
      {/* Header */}
      <header className="glass-card border-b border-slate-800/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded flex items-center justify-center"
                  style={{ 
                    background: `linear-gradient(135deg, ${currentBusiness?.accent_color || '#3B82F6'}, #F09A21)` 
                  }}
                >
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="font-montserrat text-xl font-bold text-white tracking-tight">
                    HEBEH Business
                  </h1>
                  <p className="text-xs text-slate-500 uppercase tracking-widest">
                    {currentBusiness?.logo_text || 'AUTOMOTRIZ'}
                  </p>
                </div>
              </div>
              
              {/* Business Selector */}
              <div className="ml-4">
                <BusinessSelector />
              </div>
            </div>
          </div>
          
          {/* Main Navigation Tabs */}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {/* Tabs for Automotriz and Maquinaria */}
            {businessId !== "comercializadora" && (
              <>
                <button
                  onClick={() => setMainTab("design")}
                  data-testid="main-tab-design"
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                    mainTab === "design"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                  }`}
                >
                  <Palette className="w-3.5 h-3.5" />
                  Diseño
                </button>
                <button
                  onClick={() => setMainTab("database")}
                  data-testid="main-tab-database"
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                    mainTab === "database"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                  }`}
                >
                  <Database className="w-3.5 h-3.5" />
                  BD
                </button>
                <button
                  onClick={() => setMainTab("mass-upload")}
                  data-testid="main-tab-mass-upload"
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                    mainTab === "mass-upload"
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                      : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                  }`}
                >
                  <Bot className="w-3.5 h-3.5" />
                  Trabajador Virtual
                </button>
              </>
            )}
            {businessId !== "comercializadora" && (
            <>
            <button
              onClick={() => setMainTab("design-rental")}
              data-testid="main-tab-design-rental"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "design-rental"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Truck className="w-3.5 h-3.5" />
              Renta
            </button>
            <button
              onClick={() => setMainTab("database-rental")}
              data-testid="main-tab-database-rental"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "database-rental"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Database className="w-3.5 h-3.5" />
              BD Renta
            </button>
            <button
              onClick={() => setMainTab("publications")}
              data-testid="main-tab-publications"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "publications"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Calendar className="w-3.5 h-3.5" />
              Publicaciones
            </button>
            <button
              onClick={() => setMainTab("settings")}
              data-testid="main-tab-settings"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "settings"
                  ? "bg-blue-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Settings className="w-3.5 h-3.5" />
              Config
            </button>
            <button
              onClick={() => setMainTab("leads")}
              data-testid="main-tab-leads"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "leads"
                  ? "bg-blue-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Leads
            </button>
            <button
              onClick={() => setMainTab("chatbot")}
              data-testid="main-tab-chatbot"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "chatbot"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Chatbot IA
            </button>
            <button
              onClick={() => setMainTab("appointments")}
              data-testid="main-tab-appointments"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "appointments"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <ClipboardList className="w-3.5 h-3.5" />
              Citas
            </button>
            <button
              onClick={() => setMainTab("clients")}
              data-testid="main-tab-clients"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "clients"
                  ? "bg-blue-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Clientes
            </button>
            <button
              onClick={() => setMainTab("quotations")}
              data-testid="main-tab-quotations"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "quotations"
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              Cotizaciones
            </button>
            <button
              onClick={() => setMainTab("users")}
              data-testid="main-tab-users"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "users"
                  ? "bg-purple-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <UserCog className="w-3.5 h-3.5" />
              Usuarios
            </button>
            <button
              onClick={() => setMainTab("suppliers")}
              data-testid="main-tab-suppliers"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "suppliers"
                  ? "bg-purple-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              Proveedores
            </button>
            <button
              onClick={() => setMainTab("purchase-orders")}
              data-testid="main-tab-purchase-orders"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "purchase-orders"
                  ? "bg-purple-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <ClipboardList className="w-3.5 h-3.5" />
              OC
            </button>
            <button
              onClick={() => setMainTab("company")}
              data-testid="main-tab-company"
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                mainTab === "company"
                  ? "bg-green-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
              }`}
            >
              <Settings2 className="w-3.5 h-3.5" />
              Empresa
            </button>
            </>
            )}
            
            {/* Rental Config Tab - Only for Maquinaria */}
            {businessId === "maquinaria" && (
              <button
                onClick={() => setMainTab("rental-config")}
                data-testid="rental-config-tab-btn"
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                  mainTab === "rental-config"
                    ? "bg-orange-500 text-white"
                    : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                }`}
              >
                <Truck className="w-3.5 h-3.5" />
                Config. Renta
              </button>
            )}
            
            {/* Comercializadora Tab - Only for Comercializadora */}
            {businessId === "comercializadora" && (
              <>
              <button
                onClick={() => setMainTab("comercializadora")}
                data-testid="comercializadora-tab-btn"
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                  mainTab === "comercializadora"
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                Cotizaciones
              </button>
              <button
                onClick={() => setMainTab("company")}
                data-testid="main-tab-company"
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-all ${
                  mainTab === "company"
                    ? "bg-green-500 text-white"
                    : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                }`}
              >
                <Settings2 className="w-3.5 h-3.5" />
                Empresa
              </button>
              </>
            )}
            
            {/* Link to Public Catalog - Not for Comercializadora */}
            {businessId !== "comercializadora" && (
              <a
                href={`/catalogo/${currentBusiness?.slug || 'automotriz'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium bg-gradient-to-r from-blue-500/20 to-orange-500/20 text-slate-300 hover:text-white border border-slate-700 hover:border-slate-600 transition-all"
                data-testid="catalog-link"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Catálogo
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Database Tab */}
        {mainTab === "database" && <DatabaseTab />}
        
        {/* Mass Upload Tab (Trabajador Virtual) - always mounted to persist state */}
        <div style={{ display: mainTab === "mass-upload" ? "block" : "none" }}>
          <MassUploadTab businessId={businessId} />
        </div>
        
        {/* Design Rental Tab */}
        {mainTab === "design-rental" && <DesignRentaTab />}
        
        {/* Database Rental Tab */}
        {mainTab === "database-rental" && <DatabaseRentaTab />}
        
        {/* Publications Tab */}
        {mainTab === "publications" && <PublicationsTab />}
        
        {/* Settings Tab */}
        {mainTab === "settings" && <SettingsTab />}
        
        {/* Leads Tab */}
        {mainTab === "leads" && <LeadsTab />}

        {/* Chatbot Monitor Tab */}
        {mainTab === "chatbot" && <ChatbotMonitorTab />}

        {/* Appointments Tab */}
        {mainTab === "appointments" && <AppointmentsTab />}
        
        {/* Clients Tab */}
        {mainTab === "clients" && (
          <ClientsTab 
            businessId={businessId}
            onCreateQuotation={(client) => {
              setQuotationClient(client);
              setMainTab("quotations");
            }}
          />
        )}
        
        {/* Quotations Tab */}
        {mainTab === "quotations" && (
          <QuotationsTab 
            businessId={businessId}
            initialClient={quotationClient}
          />
        )}
        
        {/* Users Tab */}
        {mainTab === "users" && <UsersTab businessId={businessId} />}
        
        {/* Suppliers Tab */}
        {mainTab === "suppliers" && <SuppliersTab businessId={businessId} />}
        
        {/* Purchase Orders Tab */}
        {mainTab === "purchase-orders" && <PurchaseOrdersTab businessId={businessId} />}
        
        {/* Company Config Tab */}
        {mainTab === "company" && <CompanyConfigTab />}
        
        {/* Rental Config Tab - Only for Maquinaria */}
        {mainTab === "rental-config" && <RentalConfigTab />}
        
        {/* Comercializadora Tab - Only for Comercializadora */}
        {mainTab === "comercializadora" && <ComercializadoraTab />}
        
        {/* Design Tab */}
        {mainTab === "design" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left Column - Input */}
          <div className="space-y-6">
            
            {/* File Upload */}
            <div className="glass-card rounded-lg p-6" data-testid="upload-section">
              <h2 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                Ficha Tecnica
                {file && (
                  <button
                    onClick={clearAll}
                    className="ml-auto text-slate-400 hover:text-white text-sm flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Limpiar
                  </button>
                )}
              </h2>
              
              <div
                className={`upload-zone rounded-lg p-8 text-center cursor-pointer ${dragOver ? 'drag-over' : ''} ${businessLoading || !businessId ? 'opacity-50 pointer-events-none' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !businessLoading && businessId && fileInputRef.current?.click()}
                data-testid="file-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                  disabled={businessLoading || !businessId}
                  data-testid="file-input"
                />
                
                {businessLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-slate-500 animate-spin" />
                    <p className="text-slate-400">Cargando configuración del negocio...</p>
                  </div>
                ) : !businessId ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Settings2 className="w-7 h-7 text-yellow-500" strokeWidth={1.5} />
                    </div>
                    <p className="text-yellow-400 font-medium">Selecciona un negocio primero</p>
                    <p className="text-slate-500 text-sm">Usa el selector de arriba para elegir el negocio</p>
                  </div>
                ) : isUploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                    <p className="text-slate-400">Subiendo archivo...</p>
                  </div>
                ) : file ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-blue-500" strokeWidth={1.5} />
                    </div>
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-slate-500 text-sm">Haz clic para cambiar</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center">
                      <Upload className="w-7 h-7 text-slate-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-white font-medium">Arrastra tu archivo aqui</p>
                      <p className="text-slate-500 text-sm mt-1">PDF, PNG, JPG o WEBP</p>
                    </div>
                  </div>
                )}
              </div>
              
              {isExtracting && (
                <div className="mt-4 flex items-center gap-3 text-blue-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Extrayendo especificaciones con IA...</span>
                </div>
              )}
            </div>

            {/* Model & Version */}
            {extractedData && (
              <div className="glass-card rounded-lg p-6 fade-in" data-testid="model-section">
                <h2 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Settings2 className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                  {currentBusiness?.id === "maquinaria" ? "Equipo y Modelo" : "Modelo y Version"}
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      {currentBusiness?.id === "maquinaria" ? "Tipo de Maquinaria" : "Nombre del Modelo"}
                    </label>
                    <input
                      type="text"
                      value={customModel}
                      onChange={(e) => setCustomModel(e.target.value.toUpperCase())}
                      placeholder={currentBusiness?.id === "maquinaria"
                        ? "Ej: RETROEXCAVADORA, COMPACTADOR, MOTONIVELADORA"
                        : "Ej: STAR 5"}
                      className="input-dark w-full px-4 py-3 rounded-lg uppercase"
                      style={{ textTransform: 'uppercase' }}
                      data-testid="model-input"
                    />
                  </div>
                  
                  {/* Versión - siempre editable */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      {currentBusiness?.id === "maquinaria" ? "Marca / Modelo / Año" : "Versión / Año"}
                      {getAvailableVersions().length > 1 && (
                        <span className="text-orange-400 ml-2">(o selecciona de las disponibles)</span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={selectedVersion}
                        onChange={(e) => setSelectedVersion(e.target.value.toUpperCase())}
                        placeholder={currentBusiness?.id === "maquinaria"
                          ? "Ej: CAT 320D 2024, JCB 3CX 2023"
                          : "Ej: COMFORT 2025"}
                        className="input-dark flex-1 px-4 py-3 rounded-lg uppercase"
                        style={{ textTransform: 'uppercase' }}
                        data-testid="version-input"
                      />
                      {/* Dropdown para versiones extraídas si hay más de una */}
                      {getAvailableVersions().length > 1 && (
                        <select
                          value={selectedVersion}
                          onChange={(e) => handleVersionChange(e.target.value)}
                          className="input-dark px-3 py-3 rounded-lg appearance-none cursor-pointer text-slate-400"
                          data-testid="version-select"
                          title="Seleccionar versión"
                        >
                          {getAvailableVersions().map((version, idx) => (
                            <option key={idx} value={version}>{version}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Precio de Venta (MXN)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="text"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder={currentBusiness?.id === "maquinaria" ? "2,500,000" : "299,900"}
                        className="input-dark w-full pl-8 pr-4 py-3 rounded-lg"
                        data-testid="price-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">
                      Precio de Compra (MXN) 
                      <span className="text-xs text-amber-500 ml-2">• Interno</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="text"
                        value={purchasePrice}
                        onChange={(e) => setPurchasePrice(e.target.value)}
                        placeholder={currentBusiness?.id === "maquinaria" ? "2,100,000" : "250,000"}
                        className="input-dark w-full pl-8 pr-4 py-3 rounded-lg border border-amber-900/50"
                        data-testid="purchase-price-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Solo visible internamente, para órdenes de compra</p>
                  </div>

                  {/* Categoría — solo para automotriz. Si se deja en blanco, el backend intenta auto-detectarla. */}
                  {currentBusiness?.id === "automotriz" && (
                    <div className="md:col-span-2">
                      <label className="block text-sm text-slate-400 mb-2">
                        Categoría del vehículo
                        <span className="text-xs text-blue-400 ml-2">• Aparece en filtros del catálogo público</span>
                      </label>
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="input-dark w-full px-4 py-3 rounded-lg"
                        data-testid="category-select"
                      >
                        <option value="">Auto-detectar (por nombre del modelo)</option>
                        <option value="Sedán">Sedán</option>
                        <option value="Camioneta">Camioneta (Pickup)</option>
                        <option value="Van">Van pasajeros</option>
                        <option value="SUV">SUV</option>
                        <option value="Hatchback">Hatchback</option>
                        <option value="Deportivo">Deportivo</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        Si dejas &quot;Auto-detectar&quot;, el sistema decide por el modelo (ej. Sentra → Sedán).
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Specifications */}
            {specifications.length > 0 && (
              <div className="glass-card rounded-lg p-6 fade-in" data-testid="specs-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-montserrat text-lg font-semibold text-white">
                    Especificaciones ({specifications.length}/9)
                  </h2>
                  {specifications.length < 9 && (
                    <button
                      onClick={addSpec}
                      className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
                      data-testid="add-spec-btn"
                    >
                      + Agregar
                    </button>
                  )}
                </div>
                
                <div className="space-y-2">
                  {specifications.map((spec, idx) => (
                    <div key={idx} className={`spec-item rounded-lg flex items-center gap-2 p-2 ${idx < 5 ? 'border-l-2 border-l-orange-500' : ''}`}>
                      <span className={`text-sm font-mono w-6 ${idx < 5 ? 'text-orange-400' : 'text-blue-500'}`}>{idx + 1}.</span>
                      <input
                        type="text"
                        value={spec}
                        onChange={(e) => updateSpec(idx, e.target.value)}
                        className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none"
                        data-testid={`spec-input-${idx}`}
                      />
                      <button
                        onClick={() => removeSpec(idx)}
                        className="text-slate-500 hover:text-red-400 transition-colors p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generate Button */}
            {extractedData && (
              <button
                onClick={generateContent}
                disabled={isGenerating || !customModel || !price || specifications.length === 0}
                className="btn-accent w-full py-4 rounded-lg font-montserrat uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generar Prompts
                  </>
                )}
              </button>
            )}

            {/* Vehicle Image Section - Shows extracted image automatically */}
            {vehicleImage && (
              <div className="glass-card rounded-lg p-6 fade-in" data-testid="vehicle-image-section">
                <h2 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Car className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                  Imagen del Vehiculo
                  <span className={`ml-2 text-xs px-2 py-1 rounded ${
                    isVehicleImageCustom
                      ? "bg-orange-500/20 text-orange-300"
                      : "bg-green-500/20 text-green-400"
                  }`}>
                    {isVehicleImageCustom ? "Imagen personalizada" : "Extraida automaticamente"}
                  </span>
                </h2>
                
                <div className="bg-slate-800/50 rounded-lg p-4 flex items-center justify-center">
                  <img 
                    src={vehicleImage} 
                    alt="Vehiculo extraido" 
                    className="max-h-40 object-contain rounded"
                    data-testid="extracted-vehicle-image"
                  />
                </div>

                {/* Botones: subir manual / restaurar la extraída */}
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setVehicleImage(ev.target.result);
                        setIsVehicleImageCustom(true);
                        toast.success("Imagen personalizada cargada — se usará como referencia");
                      };
                      reader.readAsDataURL(f);
                      e.target.value = "";
                    }}
                    style={{ display: "none" }}
                    id="vehicle-image-upload-input"
                    data-testid="vehicle-image-upload-input"
                  />
                  <label
                    htmlFor="vehicle-image-upload-input"
                    data-testid="vehicle-image-upload-btn"
                    className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/40 text-orange-300 text-xs font-medium transition"
                    title="Sube tu propia imagen del vehículo si la extraída no es correcta"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Subir otra imagen
                  </label>
                  {isVehicleImageCustom && extractedVehicleImage && (
                    <button
                      type="button"
                      onClick={() => {
                        setVehicleImage(extractedVehicleImage);
                        setIsVehicleImageCustom(false);
                        toast.info("Restaurada imagen extraída automáticamente");
                      }}
                      data-testid="vehicle-image-restore-btn"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 text-xs font-medium transition"
                    >
                      ↩️ Restaurar la extraída
                    </button>
                  )}
                </div>

                <p className="text-slate-500 text-xs mt-3 text-center">
                  Esta imagen se usara como referencia para Hailuo AI
                </p>
              </div>
            )}

            {/* Generate Images Button - Only show after prompts are generated */}
            {carousel.length > 0 && vehicleImage && (
              <button
                onClick={generateImages}
                disabled={isGeneratingImages}
                className="w-full py-4 rounded-lg font-montserrat uppercase tracking-wider flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-orange-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="generate-images-btn"
              >
                {isGeneratingImages ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generando Imagen {currentGeneratingIndex + 1}/8...
                  </>
                ) : (
                  <>
                    <ImagePlus className="w-5 h-5" />
                    Generar Imagenes con IA
                  </>
                )}
              </button>
            )}

            {/* Generated Images Results */}
            {generatedImages.length > 0 && (
              <div className="glass-card rounded-lg p-6 fade-in" data-testid="generated-images-section">
                <h2 className="font-montserrat text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                  Imagenes Generadas ({generatedImages.filter(i => i.result.success).length}/{generatedImages.length})
                </h2>
                
                <div className="space-y-3">
                  {generatedImages.map((img, idx) => {
                    // Get the image URL from different possible response structures
                    const imageUrl = img.result.data?.data?.[0]?.url || 
                                    img.result.data?.images?.[0]?.url ||
                                    img.result.data?.url;
                    
                    return (
                      <div key={idx} className={`p-4 rounded-lg border ${
                        img.result.success && imageUrl
                          ? 'bg-green-500/10 border-green-500/30' 
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{img.name}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            img.result.success && imageUrl
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {img.result.success && imageUrl ? 'Generada' : 'Error'}
                          </span>
                        </div>
                        
                        {img.result.success && imageUrl && (
                          <div className="mt-3">
                            <img 
                              src={imageUrl}
                              alt={img.name}
                              className="w-full rounded-lg border border-slate-700"
                            />
                            <div className="flex gap-2 mt-2">
                              <a
                                href={imageUrl}
                                download={`${img.name.replace(/\s/g, '_')}.png`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 py-2 rounded flex items-center justify-center gap-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors text-sm"
                              >
                                <Download className="w-4 h-4" />
                                Descargar
                              </a>
                              <button
                                onClick={() => regenerateImage(idx)}
                                disabled={regeneratingIndices.has(idx)}
                                className="flex-1 py-2 rounded flex items-center justify-center gap-2 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors text-sm disabled:opacity-50"
                                data-testid={`regenerate-btn-${idx}`}
                              >
                                {regeneratingIndices.has(idx) ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                                {regeneratingIndices.has(idx) ? "Regenerando..." : "Regenerar"}
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {(!img.result.success || !imageUrl) && (
                          <div className="mt-2">
                            <div className="p-3 bg-red-500/10 rounded mb-2">
                              <p className="text-red-400 text-sm">
                                {img.result.error || 'Error: No se pudo generar la imagen'}
                              </p>
                              {img.result.error_code && (
                                <p className="text-red-400/70 text-xs mt-1">
                                  Codigo: {img.result.error_code}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => regenerateImage(idx)}
                              disabled={regeneratingIndices.has(idx)}
                              className="w-full py-2 rounded flex items-center justify-center gap-2 bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors text-sm disabled:opacity-50"
                            >
                              {regeneratingIndices.has(idx) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                              {regeneratingIndices.has(idx) ? "Regenerando..." : "Reintentar"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Output */}
          <div className="space-y-4">
            {/* Format Tabs */}
            <div className="glass-card rounded-lg overflow-hidden">
              <div className="flex border-b border-slate-800">
                <button
                  onClick={() => setActiveTab("feed")}
                  className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-montserrat text-sm font-medium transition-all ${
                    activeTab === "feed" 
                      ? "text-white bg-blue-500/10 border-b-2 border-blue-500" 
                      : "text-slate-400 hover:text-white"
                  }`}
                  data-testid="tab-feed"
                >
                  <LayoutGrid className="w-4 h-4" />
                  Feed Carrusel ({carousel.length})
                </button>
                <button
                  onClick={() => setActiveTab("stories")}
                  className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-montserrat text-sm font-medium transition-all ${
                    activeTab === "stories" 
                      ? "text-white bg-orange-500/10 border-b-2 border-orange-500" 
                      : "text-slate-400 hover:text-white"
                  }`}
                  data-testid="tab-stories"
                >
                  <Smartphone className="w-4 h-4" />
                  Stories 9:16
                </button>
              </div>
            </div>

            {/* Content Sections */}
            {hasContent ? (
              <div className="space-y-4">
                {/* Copy Section */}
                <div className="glass-card rounded-lg p-4" data-testid="copy-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-montserrat font-medium text-white flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-blue-500" />
                      Copy para {activeTab === "feed" ? "Feed" : "Stories"}
                    </h3>
                    <button
                      onClick={() => copyToClipboard(currentCopy, `copy-${activeTab}`)}
                      className="text-slate-400 hover:text-white text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
                      data-testid={`copy-${activeTab}-copy-btn`}
                    >
                      {copiedItem === `copy-${activeTab}` ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      {copiedItem === `copy-${activeTab}` ? "Copiado" : "Copiar"}
                    </button>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed bg-[#0F1D2E] p-3 rounded-lg">
                    {currentCopy}
                  </p>
                </div>

                {/* Hashtags Section */}
                <div className="glass-card rounded-lg p-4" data-testid="hashtags-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-montserrat font-medium text-white flex items-center gap-2">
                      <Hash className="w-4 h-4 text-orange-500" />
                      Hashtags ({currentHashtags.length})
                    </h3>
                    <button
                      onClick={() => copyHashtags(currentHashtags, `hashtags-${activeTab}`)}
                      className="text-slate-400 hover:text-white text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
                      data-testid={`copy-${activeTab}-hashtags-btn`}
                    >
                      {copiedItem === `hashtags-${activeTab}` ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      {copiedItem === `hashtags-${activeTab}` ? "Copiado" : "Copiar todos"}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentHashtags.map((tag, idx) => (
                      <span 
                        key={idx} 
                        className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400 cursor-pointer hover:bg-blue-500/30 transition-colors"
                        onClick={() => copyToClipboard(tag, `tag-${idx}`)}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Prompts Section */}
                {activeTab === "feed" ? (
                  /* Carousel Prompts */
                  <div className="glass-card rounded-lg overflow-hidden" data-testid="carousel-section">
                    <div className="p-4 border-b border-slate-800">
                      <div className="flex items-center justify-between">
                        <h3 className="font-montserrat font-medium text-white flex items-center gap-2">
                          <Image className="w-4 h-4 text-blue-500" />
                          Prompts Carrusel ({activeCarouselIndex + 1}/4)
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={prevCarousel}
                            className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                            data-testid="carousel-prev"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <button
                            onClick={nextCarousel}
                            className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                            data-testid="carousel-next"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Carousel indicators */}
                      <div className="flex gap-2 mt-3">
                        {carousel.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => setActiveCarouselIndex(idx)}
                            className={`flex-1 py-2 px-2 rounded text-xs font-medium transition-all ${
                              activeCarouselIndex === idx
                                ? "bg-blue-500 text-white"
                                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                            }`}
                            data-testid={`carousel-indicator-${idx}`}
                          >
                            {idx + 1}. {item.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {carousel[activeCarouselIndex] && (
                      <>
                        <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-800">
                          <p className="text-xs text-slate-400">
                            <span className="text-blue-400 font-medium">{carousel[activeCarouselIndex].name}:</span>{" "}
                            {carousel[activeCarouselIndex].description}
                          </p>
                        </div>
                        
                        <div className="p-4 flex justify-end border-b border-slate-800">
                          <button
                            onClick={() => copyToClipboard(carousel[activeCarouselIndex].prompt, `carousel-${activeCarouselIndex}`)}
                            className="text-slate-400 hover:text-white text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
                            data-testid={`copy-carousel-${activeCarouselIndex}-btn`}
                          >
                            {copiedItem === `carousel-${activeCarouselIndex}` ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                            {copiedItem === `carousel-${activeCarouselIndex}` ? "Copiado" : "Copiar prompt"}
                          </button>
                        </div>
                        
                        <div className="divider-bicolor" />
                        
                        <div className="p-4 max-h-[350px] overflow-y-auto">
                          <pre className="prompt-display p-3 rounded-lg text-slate-300 whitespace-pre-wrap text-xs" data-testid="carousel-prompt-text">
                            {carousel[activeCarouselIndex].prompt}
                          </pre>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  /* Stories Prompt */
                  <div className="glass-card rounded-lg overflow-hidden" data-testid="stories-prompt-section">
                    <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                      <h3 className="font-montserrat font-medium text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-500" />
                        Prompt Stories 9:16
                      </h3>
                      <button
                        onClick={() => copyToClipboard(promptStories, "stories-prompt")}
                        className="text-slate-400 hover:text-white text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-800"
                        data-testid="copy-stories-prompt-btn"
                      >
                        {copiedItem === "stories-prompt" ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copiedItem === "stories-prompt" ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <div className="divider-bicolor bg-gradient-to-r from-orange-500 to-blue-500" />
                    <div className="p-4 max-h-[400px] overflow-y-auto">
                      <pre className="prompt-display p-3 rounded-lg text-slate-300 whitespace-pre-wrap text-xs" data-testid="stories-prompt-text">
                        {promptStories}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="glass-card rounded-lg p-8">
                <div className="flex items-center justify-center min-h-[400px] grid-pattern rounded-lg">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-lg border border-slate-800 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-700" strokeWidth={1} />
                    </div>
                    <p className="text-slate-600 font-medium">Esperando datos...</p>
                    <p className="text-slate-700 text-sm mt-1">
                      Sube una ficha tecnica para comenzar
                    </p>
                    <div className={`mt-6 grid gap-3 text-center ${businessId === 'maquinaria' ? 'grid-cols-3' : 'grid-cols-4'}`}>
                      <div>
                        <div className="w-8 h-10 mx-auto border border-slate-700 rounded flex items-center justify-center mb-1">
                          <span className="text-[10px] text-slate-600">1</span>
                        </div>
                        <p className="text-[10px] text-slate-600">Hook</p>
                      </div>
                      {businessId !== 'maquinaria' && (
                        <div>
                          <div className="w-8 h-10 mx-auto border border-slate-700 rounded flex items-center justify-center mb-1">
                            <span className="text-[10px] text-slate-600">2</span>
                          </div>
                          <p className="text-[10px] text-slate-600">Lifestyle</p>
                        </div>
                      )}
                      <div>
                        <div className="w-8 h-10 mx-auto border border-slate-700 rounded flex items-center justify-center mb-1">
                          <span className="text-[10px] text-slate-600">{businessId === 'maquinaria' ? '2' : '3'}</span>
                        </div>
                        <p className="text-[10px] text-slate-600">Trabajo</p>
                      </div>
                      <div>
                        <div className="w-8 h-10 mx-auto border border-slate-700 rounded flex items-center justify-center mb-1">
                          <span className="text-[10px] text-slate-600">{businessId === 'maquinaria' ? '3' : '4'}</span>
                        </div>
                        <p className="text-[10px] text-slate-600">Ficha</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Confirm Content Button - Only show when images are generated */}
          {generatedImages.length > 0 && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={confirmContent}
                disabled={confirmProgress?.status === 'running'}
                className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-montserrat font-semibold uppercase tracking-wider flex items-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-content-btn"
              >
                {confirmProgress?.status === 'running' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5" />
                )}
                {confirmProgress?.status === 'running' ? "Guardando..." : "Confirmar Contenido"}
              </button>
            </div>
          )}
        </div>
        )}
      </main>

      {/* Modal de progreso al confirmar contenido */}
      {confirmProgress && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="confirm-progress-modal"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              {confirmProgress.status === 'running' && (
                <>
                  <Loader2 className="w-12 h-12 text-orange-400 animate-spin mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">Guardando publicación</h3>
                  <p className="text-slate-300 text-sm mb-4">{confirmProgress.message}</p>
                  <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-300"
                      style={{ width: `${(confirmProgress.step / confirmProgress.total) * 100}%` }}
                      data-testid="confirm-progress-bar"
                    />
                  </div>
                  <p className="text-slate-500 text-xs mt-3">
                    Paso {confirmProgress.step} de {confirmProgress.total} — {Math.round((confirmProgress.step / confirmProgress.total) * 100)}%
                  </p>
                </>
              )}
              {confirmProgress.status === 'success' && (
                <>
                  <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">¡Listo!</h3>
                  <p className="text-slate-300 text-sm mb-6">{confirmProgress.message}</p>
                  <button
                    onClick={() => setConfirmProgress(null)}
                    className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition"
                    data-testid="confirm-progress-accept-btn"
                  >
                    Aceptar
                  </button>
                </>
              )}
              {confirmProgress.status === 'error' && (
                <>
                  <X className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">Hubo un error</h3>
                  <p className="text-slate-300 text-sm mb-6 break-words">{confirmProgress.message}</p>
                  <button
                    onClick={() => setConfirmProgress(null)}
                    className="px-6 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition"
                    data-testid="confirm-progress-close-btn"
                  >
                    Cerrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="mt-16 border-t border-slate-800/50 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-600 text-sm">
            © 2026 HEBEH Business. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
